import type { LanguageJourney } from "./core";
import {
  PLAN_SHAPE_BY_TIME,
  START_CHAPTER_BY_ABILITY,
  type SpanishJourneyPlan,
} from "./spanish-types";

type CourseIdentity = Pick<LanguageJourney, "courseVersion"> & {
  chapters: Array<{ id: string; chapterNumber: number }>;
};

/** The same validation for server/full-course and cold-client launch metadata. */
export function validateSpanishPlanMetadata(
  plan: SpanishJourneyPlan,
  version: number,
  course: CourseIdentity
) {
  if (plan.catalogVersion !== version || plan.courseVersion !== course.courseVersion) {
    throw new Error("This plan was created for an older Spanish Journey catalog.");
  }
  const profile = plan.profile;
  const startIndex = profile.startingChapterId
    ? course.chapters.findIndex((chapter) => chapter.id === profile.startingChapterId)
    : course.chapters.findIndex(
        (chapter) => chapter.chapterNumber === START_CHAPTER_BY_ABILITY[profile.ability]
      );
  const { sessions } = PLAN_SHAPE_BY_TIME[profile.timeAvailable];
  if (startIndex < 0 || course.chapters.length - startIndex < sessions) {
    throw new Error("Choose a reviewed Spanish chapter with room for the plan.");
  }
  const candidates = course.chapters.slice(startIndex, startIndex + sessions + 4);
  const candidateNumbers = new Map(
    candidates.map((chapter) => [chapter.id, chapter.chapterNumber])
  );
  const numbers = plan.items.map((item) => candidateNumbers.get(item.chapterId));
  if (numbers.some((number) => number === undefined)) {
    throw new Error("The plan contains a chapter outside its reviewed starting window.");
  }
  if (new Set(plan.items.map((item) => item.chapterId)).size !== plan.items.length) {
    throw new Error("The plan contains a duplicate chapter.");
  }
  if (numbers[0] !== candidates[0]?.chapterNumber) {
    throw new Error("The plan must start at the reviewed entry chapter for this level.");
  }
  for (let index = 1; index < numbers.length; index++) {
    if (numbers[index]! <= numbers[index - 1]! || numbers[index]! - numbers[index - 1]! > 2) {
      throw new Error("The plan chapter order would break the reviewed progression.");
    }
  }
  return plan;
}
